import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { iconoirNavArrowRight } from '@ng-icons/iconoir';
import { filter } from 'rxjs/operators';
import { BreadcrumbService } from '../../../../services/breadcrumb.service';

interface Crumb {
  label: string;
  url?: string;
  ellipsis?: boolean;
}

@Component({
  selector: 'app-breadcrumb',
  imports: [NgIcon, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [provideIcons({ iconoirNavArrowRight })],
  template: `
    @if (displayCrumbs().length > 1) {
      <nav class="breadcrumb" aria-label="Fil d'Ariane">
        @for (crumb of displayCrumbs(); track $index; let last = $last) {
          @if (crumb.ellipsis) {
            <span class="crumb-label">…</span>
          } @else if (crumb.url) {
            <a [routerLink]="crumb.url" class="crumb-link">{{ crumb.label }}</a>
          } @else {
            <span [class]="last ? 'crumb-current' : 'crumb-label'" [attr.aria-current]="last ? 'page' : null">{{ crumb.label }}</span>
            @if (last && dynamicLabel()) {
              <span class="crumb-pipe">|</span>
              <span class="crumb-dynamic">{{ dynamicLabel() }}</span>
            }
          }
          @if (!last) {
            <ng-icon name="iconoirNavArrowRight" size="14" class="crumb-sep" />
          }
        }
      </nav>
    }
  `,
  styles: `
    :host { display: block; }

    .breadcrumb {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 0;
      min-width: 0;
    }

    .crumb-link {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--text-secondary);
      text-decoration: none;
      border-radius: var(--radius-pill);
      white-space: nowrap;
      transition: color 0.15s;

      &:hover {
        color: var(--text-accent);
      }

      &:focus-visible {
        outline: 2px solid var(--focus-ring-color);
        outline-offset: 2px;
      }
    }

    .crumb-label {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--text-tertiary);
      white-space: nowrap;
    }

    .crumb-current {
      font-size: 0.8125rem;
      font-weight: 700;
      color: var(--text-primary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .crumb-sep {
      color: var(--border-dashed);
      flex-shrink: 0;
    }

    .crumb-pipe {
      color: var(--border-dashed);
      margin: 0 6px;
      font-size: 0.8125rem;
    }

    /* Libellé dynamique (nom de l'élément ouvert) : pastille de ton orange */
    .crumb-dynamic {
      font-size: 0.75rem;
      font-weight: 700;
      padding: 2px 10px;
      border-radius: var(--radius-pill);
      background: var(--tone-orange-bg);
      color: var(--tone-orange-fg);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }
  `,
})
export class BreadcrumbComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private breadcrumbService = inject(BreadcrumbService);

  crumbs = signal<Crumb[]>([]);
  dynamicLabel = this.breadcrumbService.dynamicLabel;

  /**
   * Maillons de la route, complétés par la page : maillon de contexte (ex. le projet, inséré
   * après « Projets ») et libellé du dernier maillon (ex. la vue active Board / Liste…).
   */
  private withContext = computed<Crumb[]>(() => {
    const all = [...this.crumbs()];
    const context = this.breadcrumbService.context();
    if (context) {
      const index = all.findIndex(c => c.url === context.after);
      const at = index >= 0 ? index + 1 : -1;
      if (at > 0 && all[at]?.label !== context.label) {
        all.splice(at, 0, { label: context.label, url: context.url });
      }
    }
    const leaf = this.breadcrumbService.leafLabel();
    const last = all[all.length - 1];
    if (leaf && all.length > 2 && last && last.label !== context?.label) {
      all[all.length - 1] = { ...last, label: leaf };
    }
    // Contexte seul en dernier (page sans maillon propre) : pas de lien vers soi-même.
    if (all.length > 1 && all[all.length - 1].url) {
      all[all.length - 1] = { ...all[all.length - 1], url: undefined };
    }
    return all;
  });

  displayCrumbs = computed<Crumb[]>(() => {
    const all = this.withContext();
    if (all.length < 5) return all;
    return [all[0], { label: '…', ellipsis: true }, ...all.slice(-2)];
  });

  constructor() {
    // Publie le fil complet (avant troncature « … ») pour l'en-tête mobile.
    effect(() => this.breadcrumbService.trail.set(this.withContext()));
    this.destroyRef.onDestroy(() => this.breadcrumbService.trail.set([]));
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.build());

    this.build();
  }

  private build(): void {
    const crumbs: Crumb[] = [{ label: 'Accueil', url: '/dashboard' }];
    this.walk(this.route.root, '', crumbs);

    // Le dernier maillon perd son lien dans withContext (après insertion du contexte).
    this.crumbs.set(crumbs);
  }

  private walk(route: ActivatedRoute | null, parentUrl: string, crumbs: Crumb[]): void {
    if (!route) return;

    const segments = route.snapshot.url.map(s => s.path);
    let url = parentUrl;
    if (segments.length) url += '/' + segments.join('/');

    const header = route.snapshot.data['pageHeader'] as { breadcrumb?: string; breadcrumbLink?: boolean; breadcrumbUrl?: string; breadcrumbGroup?: string } | undefined;
    // Rubrique du menu (sans lien) avant la page, quand la route parente n'en porte pas une seule
    // pour toutes ses enfants (ex. réglages RH : « Rémunération » ou « Configuration »).
    if (header?.breadcrumbGroup && !crumbs.some(c => c.label === header.breadcrumbGroup)) {
      crumbs.push({ label: header.breadcrumbGroup, url: undefined });
    }
    if (header?.breadcrumb) {
      const duplicate = crumbs.some(c => c.label === header.breadcrumb);
      if (!duplicate) {
        const hasLink = header.breadcrumbLink !== false;
        const resolvedUrl = header.breadcrumbUrl ?? (url || '/');
        crumbs.push({ label: header.breadcrumb, url: hasLink ? resolvedUrl : undefined });
      }
    }

    if (route.firstChild) {
      this.walk(route.firstChild, url, crumbs);
    }
  }
}
